import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UserManager, UserManagerSettings, User } from 'oidc-client';
import { BehaviorSubject, Observable } from "rxjs";
import { UserAccount } from "src/app/models";
import { environment } from "src/environments/environment";
import { Constants } from 'src/app/shared/models/constants';
import { Helpers } from 'src/app/helpers/helpers';
import { Router } from '@angular/router';
import moment from 'moment';

@Injectable({ providedIn: 'root' })
export class SecurityService {
  private USERSESSION_SERVICE_URL = environment.userSessionService.url + environment.userSessionService.apiVersion;
  // Observable navItem source
  private authNavStatusSource = new BehaviorSubject<boolean>(false);
  private currentUserSubject!: BehaviorSubject<UserAccount>;
  public currentUser!: Observable<UserAccount>;
  // Observable navItem stream
  authNavStatus$ = this.authNavStatusSource.asObservable();

  private manager = new UserManager(getClientSettings());
  private user!: User | null;

  constructor(private http: HttpClient, private router: Router) {

    this.manager.getUser().then(user => {
      this.user = user;
      this.authNavStatusSource.next(this.isAuthenticated());
      let userAccount = this.transformUserObject(<User>user);
      this.currentUserSubject = new BehaviorSubject<UserAccount>(userAccount!);
      this.currentUser = this.currentUserSubject.asObservable();
    });

    this.manager.events.addUserLoaded((user: User) => {
      this.updateUserInStorage(user);
      if (window.location.href.toLocaleLowerCase().indexOf("mytraining") != -1) {
        this.manageUserSession(1);
      }
    });

    this.manager.events.addSilentRenewError(() => {
      console.error('Silent Renew Error：', arguments);
    });

  }
  public get currentUserValue(): UserAccount {
    let userObject = localStorage.getItem(Constants.LOCALSTORE_USEROBJECT);
    if (userObject != null && userObject != undefined) {
      let userAccount: UserAccount = <UserAccount>(JSON.parse(decodeURIComponent(escape(atob(userObject!)))));
      if (userAccount.Expiration != undefined) {
        var Expiration = new Date(userAccount.Expiration * 1000);
        if (Expiration > new Date()) {
          this.currentUserSubject.next(userAccount!);
          return this.currentUserSubject?.value;
        } else {
          this.clearLocalStorage();
          return null!;
        }
      } else {
        this.clearLocalStorage();
        return null!;
      }
    } else {
      return null!;
    }
  }

  login() {
    return this.manager.signinRedirect();
  }

  logout(insertReturnUrl = false) {
    // remove user from local storage to log user out
    this.manageUserSession(0).then(() => {
      this.currentUserSubject.unsubscribe();
      this.clearLocalStorage();
      this.signout();
      if (insertReturnUrl) {
        let returnUrl = this.router.routerState.snapshot.url;
        if (!Helpers.isNullOrUndefined(returnUrl) && returnUrl != '') {
          localStorage.setItem(Constants.LOCALSTORE_RETURNURL, returnUrl);
        }
      }
    });
  }

  clearLocalStorage() {
    let dontShowNotificationIds = localStorage.getItem(Constants.LOCALSTORE_CLOSEDNOTIFICATIONIDS);
    let dontShowSiteVieryNotification = localStorage.getItem(Constants.LOCALSTORE_DONTSHOWSITEVERIFYNOTIFICATION);
    let signingid ;
    let signingValue;
   for (var key in localStorage){
    console.log("key " + key);
    if(key.startsWith('signingId_',0)){
      signingValue = localStorage.getItem(key);
      signingid = key;
    }
 }
    localStorage.clear();
    sessionStorage.clear();
    if (dontShowNotificationIds != undefined) {
      localStorage.setItem(Constants.LOCALSTORE_CLOSEDNOTIFICATIONIDS, dontShowNotificationIds);
    }
    if (dontShowSiteVieryNotification != undefined) {
      localStorage.setItem(Constants.LOCALSTORE_DONTSHOWSITEVERIFYNOTIFICATION, dontShowSiteVieryNotification);
    }
    if (signingValue != undefined && signingid != undefined) {
      localStorage.setItem(signingid,signingValue );
    }
  }

  getUser() {
    return this.manager.getUser();
  }
  completeAuthentication() {
    return this.manager.getUser().then((user: any) => {
      if (user) {
        this.updateUserInStorage(user);
      }
    });
  }

  private updateUserInStorage(user: User) {
    this.user = <User>user;
    let userAccount = this.transformUserObject(this.user);
    this.currentUserSubject.next(userAccount!);

    // Don't save to localStorage if password is expired — let auth-callback handle the redirect
    const passwordChangedDate = user.profile[Constants.IDENTITY_CLAIM_PASSWORDCHANGEDDATE];
    if (typeof passwordChangedDate === "string" && passwordChangedDate.trim().length > 0) {
      const diffDays = moment.utc().diff(moment(passwordChangedDate), 'days');
      if (diffDays > environment.passwordExpirationDays) {
        return;
      }
    }

    localStorage.setItem(Constants.LOCALSTORE_USEROBJECT, btoa(unescape(encodeURIComponent(JSON.stringify(userAccount)))));
    this.authNavStatusSource.next(this.isAuthenticated());
  }

  private transformUserObject(_user: User) {
    if (_user != null) {
      if (_user.profile != null) {
        let userAccount: UserAccount = {
          UserGuid: _user.profile.sub,
          UserID: _user.profile[Constants.IDENTITY_CLAIM_NAMEIDENTIFIER],
          FirstName: _user.profile[Constants.IDENTITY_CLAIM_FIRSTNAME],
          FullName: _user.profile[Constants.IDENTITY_CLAIM_FULLNAME],
          LastName: _user.profile[Constants.IDENTITY_CLAIM_LASTNAME],
          PasswordChangedDate: _user.profile[Constants.IDENTITY_CLAIM_PASSWORDCHANGEDDATE],
          UserName: _user.profile[Constants.IDENTITY_CLAIM_EMAIL],
          SessionId: _user.profile.sid as string,
          AuthTime: _user.profile.auth_time,

          IdToken: _user.id_token,
          MedPointPlatformId: 1,
          Password: "",
          Scope: _user.scope,
          SessionState: _user.session_state,
          Expiration: _user.expires_at,

          Token: _user.access_token,
          Idp: _user.profile["idp"]
        };
        return userAccount;
      }
    }
    return null;
  }

  isAuthenticated(): boolean {
    return this.user != null && !this.user.expired;
  }

  get authorizationHeaderValue(): string {
    return this.user != null ? `${this.user.token_type} ${this.user.access_token}` : "";
  }

  get name(): string {
    return this.user != null ? this.user.profile.name != undefined ? this.user.profile.name : '' : '';
  }

  async signout() {
    this.currentUserSubject.unsubscribe();
    const idToken = this.user == null ? '' : this.user.id_token;
    await this.manager.signoutRedirect(({ 'id_token_hint': idToken }));
  }

  async signinRedirect() {
    this.currentUserSubject.unsubscribe();
    const idToken = this.user == null ? '' : this.user.id_token;
    await this.manager.signinRedirect(({ 'id_token_hint': idToken }));
  }

  checkPlatformAccess(userId: number) {
    var postData = {
      MedPointPlatformId: Helpers.getPlatformId(),
      Userid: userId
    }
    return this.http.post<any>(environment.identityService.url + environment.identityService.apiVersion + "Users/GetPlatformAccessforUser", postData);
  }

  manageUserSession(statusId: number) {
    //statusId: 1 is active user sessions, -1 is user session expiring due to inactivity, 0 is user calls the logout

    return new Promise<number>((resolve, reject) => {
      if (this.currentUserValue != null) {
        var userSession = {
          UserId: this.currentUserValue.UserGuid,
          ClientId: environment.SSO.clientId,
          Status: statusId,
          SessionId: this.currentUserValue.SessionId,
          SessionIdleTime: environment.idleTimeoutInMinutes - 1
        };
        this.http.post<number>(this.USERSESSION_SERVICE_URL + "user/manageusersession/" + environment.SSO.COSMOSDBTableName, userSession).toPromise()
          .then((response) => {
            if (response == -1) {
              this.clearLocalStorage();
              this.signout();
              this.logout(statusId == -1);
              resolve(response);
            } else if (response != null) {
              resolve(response);
            }
          },
            err => {
              reject(err);
            }
          );
      } else {
        resolve(1);
      }
    });

  }
}



export function getClientSettings(): UserManagerSettings {
  return {
    authority: environment.SSO.secureAppUrl,
    client_id: environment.SSO.clientId,
    redirect_uri: Helpers.getApplicationUrl() + '/signin-callback.html',
    post_logout_redirect_uri: Helpers.getApplicationUrl() + '/',
    response_type: 'id_token token',
    scope: 'openid profile identitysvc triportnewssvc triportcalendareventsvc triportleaderboardsvc usersessionsvc triportreportsvc instantnotification storagesvc triporttoolssvc triportcommunicationsvc triportcoresvc triportdocumentsvc triportformssvc triporttrainingsvc discussionsvc triportfaqsvc assetsvc coresvc',
    filterProtocolClaims: true,
    loadUserInfo: true,
    automaticSilentRenew: true,
    silent_redirect_uri: Helpers.getApplicationUrl() + '/silent-renew.html'
  };
}


